import { useEffect, useRef } from "react";

import { useForm } from "../../useForm";

import { classNames } from "primereact/utils";
import type { ProductDropdownOption } from "../../../../types/orders";

interface ProductQuantityForm {
  id: number;
  quantity: number | string | null;
  price: number | string;
  discount: number | string;
  tax_rate: number | string;
}

interface AddProductToOrderFormProps {
  product: ProductDropdownOption;
  confirmAction: (product: ProductDropdownOption & { id: number; quantity: number | string | null }) => void;
  setOpen: () => void;
  isEdit?: boolean;
}

export default function AddProductToOrderForm(props: AddProductToOrderFormProps) {
  const { product, confirmAction, setOpen } = props;
  const initialFValues: ProductQuantityForm = {
    id: 0,
    quantity: null,
    price: Number(product.unit_price ?? product.price ?? 0),
    discount: typeof product.discount === "number" ? product.discount : 0,
    tax_rate: typeof product.tax_rate === "number" ? product.tax_rate : 0,
  };

  const validate = (fieldValues: Partial<ProductQuantityForm> = values) => {
    let temp = { ...errors };

    temp.quantity = null;
    if (Number(fieldValues.quantity ?? 0) < 1) {
      temp.quantity = "Order quantity must be more than 0.";
    }
    temp.price = Number(fieldValues.price) >= 0 ? null : "Unit price must be zero or more.";
    temp.discount = Number(fieldValues.discount) >= 0 ? null : "Discount must be zero or more.";
    const taxRate = Number(fieldValues.tax_rate);
    temp.tax_rate = taxRate >= 0 && taxRate <= 100 ? null : "Tax must be between 0 and 100.";

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    validate
  );

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const onSubmit = () => {
    if (validate()) {
      confirmAction({
        ...product,
        id: -1,
        quantity: values.quantity,
        price: Number(values.price),
        discount: Number(values.discount),
        tax_rate: Number(values.tax_rate),
      });
    } else {
    }
  };

  return (
    <div className="justify-between">
      <div>
        <div className="flex items-center justify-between flex-wrap sm:flex-nowrap">
          <div className="mt-2">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {product.product_name} - ({product.sku})
            </h3>
            <h3 className="text-md leading-6 font-medium text-gray-900">{product.description}</h3>
          </div>
        </div>

        {/* here */}
        <div className="pt-3 pb-5 gap-4">
          <div className="bg-white sm:rounded-xl">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex space-x-2 mb-1">
                  <label
                    htmlFor="quantity"
                    className={classNames("text-sm", "block font-medium text-gray-900")}
                  >
                    Amount to order
                  </label>
                </div>
                <input
                  name="quantity"
                  onChange={handleInputChange}
                  value={values.quantity ?? ""}
                  ref={inputRef}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  onKeyDown={(e) => (e.key === "Enter" ? onSubmit() : null)}
                />
                {errors.quantity ? (
                  <p className="font-medium text-red-500">{errors.quantity}</p>
                ) : null}
              </div>
              {([
                ["price", "Unit price"],
                ["discount", "Discount"],
                ["tax_rate", "Tax %"],
              ] as const).map(([name, label]) => (
                <div key={name}>
                  <label htmlFor={name} className="text-sm block font-medium text-gray-900">
                    {label}
                  </label>
                  <input
                    name={name}
                    onChange={handleInputChange}
                    value={values[name]}
                    type="number"
                    min={0}
                    max={name === "tax_rate" ? 100 : undefined}
                    step="0.01"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  />
                  {errors[name] ? <p className="font-medium text-red-500">{errors[name]}</p> : null}
                </div>
              ))}
            </div>
          </div>

          <div className="sm:flex-no-wrap my-auto flex items-center">
            <button
              onClick={() => onSubmit()}
              className="inline-flex justify-center mt-3 py-35-custom px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center"
            >
              OK
            </button>
            <button
              onClick={() => setOpen()}
              className="ml-2 inline-flex justify-center mt-3 py-35-custom px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 items-center"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
